/**
 * SkyManager - Handles the gradient sky sphere
 *
 * The sky is rendered as a large inverted sphere with vertex colors
 * creating a gradient from horizon to zenith. The sphere follows
 * the camera so the sky is always visible.
 *
 * Responsibilities:
 * - Create the sky sphere geometry and material
 * - Update colors during day/night transitions
 * - Keep the sphere centered on the camera
 */

import * as THREE from 'three';
import { SKY } from '../config/constants.js';

export class SkyManager {
    /**
     * Create a new SkyManager
     * @param {THREE.Scene} scene - The Three.js scene to add the sky to
     * @param {THREE.Camera} camera - The camera to follow
     * @param {ScenePreloader} [preloader] - Optional preloader for progress tracking
     */
    constructor(scene, camera, preloader = null) {
        this.scene = scene;
        this.camera = camera;
        this.skySphere = null;

        // Self-register for preload tracking with dispose callback
        this._onPreloadComplete = preloader?.register('sky', {
            dispose: () => this.dispose()
        });

        // Current colors (will be updated by color cycle)
        this.topColor = SKY.DEFAULT_TOP_COLOR;
        this.bottomColor = SKY.DEFAULT_BOTTOM_COLOR;
    }

    /**
     * Initialize the sky sphere
     * Call this after scene and camera are ready
     */
    initialize() {
        this.createSkySphere();
        this._onPreloadComplete?.();
    }

    /**
     * Create the sky sphere with gradient vertex colors
     * @private
     */
    createSkySphere() {
        const radius = SKY.RADIUS;
        const segments = SKY.SEGMENTS;

        const geometry = new THREE.SphereGeometry(radius, segments, segments);

        // Get the position attribute to calculate vertex colors
        const positions = geometry.attributes.position;
        const colors = [];

        // Create gradient from top (zenith) to bottom (horizon)
        for (let i = 0; i < positions.count; i++) {
            const y = positions.getY(i);

            // Normalize Y position (-radius to +radius) to (0 to 1)
            // 0 = bottom (horizon), 1 = top (zenith)
            const t = (y + radius) / (radius * 2);

            // Interpolate between bottom and top colors
            const color = this.lerpColorToRGB(this.bottomColor, this.topColor, t);
            colors.push(color.r, color.g, color.b);
        }

        // Add color attribute to geometry
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

        // Create material that uses vertex colors (unlit, no fog)
        const material = new THREE.MeshBasicMaterial({
            vertexColors: true,
            side: THREE.BackSide, // Render inside of sphere
            fog: false,           // Sky shouldn't be affected by fog
            depthWrite: false     // Sky should render behind everything
        });

        this.skySphere = new THREE.Mesh(geometry, material);
        this.skySphere.renderOrder = -1; // Render sky first (behind everything)
        this.scene.add(this.skySphere);
    }

    /**
     * Update sky colors (call during day/night transitions)
     * @param {number} topColor - Hex color for zenith (top of sky)
     * @param {number} bottomColor - Hex color for horizon (bottom of sky)
     */
    setColors(topColor, bottomColor) {
        this.topColor = topColor;
        this.bottomColor = bottomColor;
        this.updateColors();
    }

    /**
     * Update the vertex colors of the sky sphere
     * @private
     */
    updateColors() {
        if (!this.skySphere) return;

        const geometry = this.skySphere.geometry;
        const positions = geometry.attributes.position;
        const colors = geometry.attributes.color;
        const radius = SKY.RADIUS;

        // Update each vertex color based on its Y position
        for (let i = 0; i < positions.count; i++) {
            const y = positions.getY(i);
            const t = (y + radius) / (radius * 2);

            const color = this.lerpColorToRGB(this.bottomColor, this.topColor, t);
            colors.setXYZ(i, color.r, color.g, color.b);
        }

        colors.needsUpdate = true;
    }

    /**
     * Update sky position to follow camera
     * Call this every frame
     */
    update() {
        if (!this.skySphere) return;

        // Keep sky sphere centered on camera so it follows the player
        this.skySphere.position.copy(this.camera.position);
    }

    /**
     * Interpolate between two hex colors and return RGB object (0-1 range)
     * @param {number} color1 - First hex color
     * @param {number} color2 - Second hex color
     * @param {number} t - Interpolation factor (0-1)
     * @returns {{r: number, g: number, b: number}} RGB values in 0-1 range
     * @private
     */
    lerpColorToRGB(color1, color2, t) {
        const r1 = (color1 >> 16) & 0xff;
        const g1 = (color1 >> 8) & 0xff;
        const b1 = color1 & 0xff;

        const r2 = (color2 >> 16) & 0xff;
        const g2 = (color2 >> 8) & 0xff;
        const b2 = color2 & 0xff;

        return {
            r: (r1 + (r2 - r1) * t) / 255,
            g: (g1 + (g2 - g1) * t) / 255,
            b: (b1 + (b2 - b1) * t) / 255
        };
    }

    /**
     * Clean up resources
     */
    dispose() {
        if (this.skySphere) {
            this.skySphere.geometry.dispose();
            this.skySphere.material.dispose();
            this.scene.remove(this.skySphere);
            this.skySphere = null;
        }
    }
}
